import React, { useCallback, useEffect, useState } from "react";
import "./profile.css";
import { useKeycloak } from "@react-keycloak/web";
import { useSearchParams } from "react-router-dom";
import { ProvidersService } from "../../services/ProvidersService";
import { MasterDataService } from "../../services/MasterDataService";
import {
  SelectedProviderProvider,
  useSelectedProvider,
} from "../../contexts/SelectedProviderContext";
import BusinessProfileGate from "../../components/BusinessProfileGate";
import hash from "object-hash";
import { MUI_CATEGORY_ICONS, LEGACY_CATEGORY_ICONS, DEFAULT_CATEGORY_ICON_BY_KEY } from "../../components/categoryIconRegistry";

import {
  Box, Button,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  IconButton, Typography, Container, Snackbar, Alert
} from '@mui/material';
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DashboardIcon from "@mui/icons-material/Dashboard";
import BusinessIcon from "@mui/icons-material/Business";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import PaymentIcon from "@mui/icons-material/Payment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PolicyIcon from "@mui/icons-material/Policy";
import RateReviewIcon from "@mui/icons-material/RateReview";
import RoomServiceIcon from "@mui/icons-material/RoomService";
import AddIcon from "@mui/icons-material/Add";
import UseWindowDimensions from "../../components/GetWindowDimensions";
import { GeneralTab } from "./generalTab";
import { ServicesProvidedTab } from "./servicesProvidedTab";
import { PromotionsTab } from "./promotionsTab";
import { PaymentsTab } from "./paymentsTab";
import { CertificationsTab } from "./certificationsTab";
import { SocialTab } from "./socialTab";
import { PoliciesTab } from "./policiesTab";
import { ReviewsTab } from "./reviewsTab";
import { CalendarTab } from "./calendarTab";
import { DashboardTab } from "./dashboardTab";
import { UploadPhotosTab } from "./uploadPhotosTab";
import {Constants} from "../../Constants";
import {Helmet} from "react-helmet";

/**
 * Outer Profile component: sets up the multi-provider selection context and
 * defers to the gate. The gate handles loading / 0-provider / multi-provider
 * selection states; only once a provider is selected does ProfileContent
 * render. ProfileContent then drives the existing tab UI with the chosen
 * provider as its seed.
 */
const Profile = (props) => {
  const { keycloak } = useKeycloak();

  // Memoize so SelectedProviderContext doesn't see a new fn on every render.
  const fetchProviders = useCallback(
    () => ProvidersService.getMyProviders(keycloak),
    [keycloak],
  );

  return (
    <SelectedProviderProvider fetchProviders={fetchProviders}>
      <BusinessProfileGate>
        <ProfileContent {...props} />
      </BusinessProfileGate>
    </SelectedProviderProvider>
  );
};

// Tab to URL slug mapping (case-insensitive) - exported for use in other components
export const TAB_SLUG_MAP = {
  "Dashboard": "dashboard",
  "General": "general",
  "Photos": "photos",
  "Services": "services",
  "Payments": "payments",
  "Calendar": "calendar",
  "Policies": "policies",
  "Reviews": "reviews",
  "Promotions": "promotions",
  "Certifications": "certifications",
  "Social": "social",
};

// Reverse map: slug to tab label
const SLUG_TAB_MAP = Object.entries(TAB_SLUG_MAP).reduce((acc, [label, slug]) => {
  acc[slug.toLowerCase()] = label;
  return acc;
}, {});

export const getSlugFromLabel = (label) => TAB_SLUG_MAP[label] || null;

export const getLabelFromSlug = (slug) => SLUG_TAB_MAP[slug.toLowerCase()] || null;

export const updateTabInUrl = (tabLabel, searchParams, setSearchParams) => {
  const slug = getSlugFromLabel(tabLabel);
  if (slug) {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", slug);
    setSearchParams(newParams);
  }
};

// Map tab labels to their Material UI icons
const TAB_ICONS = {
  "Dashboard": <DashboardIcon />,
  "General": <BusinessIcon />,
  "Photos": <PhotoCameraIcon />,
  "Services": <ManageAccountsIcon />,
  "Payments": <PaymentIcon />,
  "Calendar": <CalendarMonthIcon />,
  "Policies": <PolicyIcon />,
  "Reviews": <RateReviewIcon />,
};

// Helper to render the appropriate icon for a tab, with fallback
const renderTabIcon = (text) => {
  return TAB_ICONS[text] || <DashboardIcon />;
};

const ProfileContent = (props) => {

  const selectorButtonStyle = {
    background: "white",
    marginLeft: ".5em",
    marginRight: ".5em",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: "8px"
  };

  const selectorSingleColumnButtonStyle = {
    background: "white",
    marginBottom: "1em",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: "8px"
  };

  const drawerWidth = 240;
  const { window } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const { keycloak } = useKeycloak();
  const { width } = UseWindowDimensions();
  const [showModal, setShowModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState("Add New")
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize selectedTab from URL if present, otherwise default to Dashboard
  const getInitialTab = () => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      const tabLabel = getLabelFromSlug(tabParam);
      if (tabLabel) {
        return tabLabel;
      }
    }
    return "Dashboard";
  };

  const [selectedTab, setSelectedTab] = useState(getInitialTab());
  const [services, setServices] = useState([])
  const [addNew, setAddNew] = useState(false);
  const [buttonStyle, setButtonStyle] = useState(selectorButtonStyle);

  // Watch for URL parameter changes and update the selected tab accordingly
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      const tabLabel = getLabelFromSlug(tabParam);
      if (tabLabel) {
        setSelectedTab(tabLabel);
      } else {
        // Unrecognized slug, fall back to Dashboard
        setSelectedTab("Dashboard");
      }
    } else {
      // No tab parameter, default to Dashboard
      setSelectedTab("Dashboard");
    }
  }, [searchParams]);
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const [selectedServiceIndex, setSelectedServiceIndex] = useState();
  const [saveTimeoutHandle, setSaveTimeoutHandle] = useState(0);
  const [numberOfUpdates, setNumberOfUpdates] = useState(0)
  const [isMobile, setIsMobile] = useState(width<=Constants.MOBILE_WIDTH)
  const [providerHash, setProviderHash] = useState(null);
  const [categoryConfigs, setCategoryConfigs] = useState([]);

  let subscriberId = keycloak.tokenParsed.sub;

  // Provider state is seeded from the selection context and kept in sync with
  // local edits. Switching businesses (via the header button or the gate)
  // clears local state and re-seeds from the newly-selected provider.
  const {
    providers: ownedProviders,
    selectedProvider,
    selectedProviderId,
    setSelectedProviderId,
  } = useSelectedProvider();
  const [provider, setProvider] = useState(selectedProvider);
  const [isSaved, setIsSaved] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  // Derive current save state message and severity
  const getSaveStateMessage = () => {
    if (saving) {
      return { message: 'Saving…', severity: 'info' };
    } else if (saveError) {
      return { message: "Couldn't save changes", severity: 'error' };
    } else if (!isSaved) {
      return { message: 'Unsaved changes', severity: 'warning' };
    } else {
      return { message: 'Saved', severity: 'success' };
    }
  };

  // When "Services" tab is selected (from URL or navigation), expand the
  // submenu and auto-select the first service if available. This ensures the user
  // can act immediately when landing on this tab via a deep link or dashboard link.
  // We deliberately exclude selectedServiceIndex from dependencies to prevent loops:
  // we only auto-select when it's undefined (fresh arrival), not every time it changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedTab === "Services") {
      setOpenSubMenu(true);

      // If there are services but none selected, select the first one
      if (provider?.servicesProvided?.length > 0 && selectedServiceIndex === undefined) {
        setSelectedServiceIndex(0);
        setSelectedIndex(provider.servicesProvided[0].name);
      }
    }
  }, [selectedTab, provider]);

  // Promotions, Certifications, and Social tabs are stub components with no
  // state, save handlers, or backing API methods. Hidden until implemented.
  // The conditional renderers below are kept so re-enabling is a one-line
  // change (just add the entry back to this list).
  const listOfTabs = ["Dashboard",
    "General",
    "Photos",
    "Services",
    "Payments",
    "Calendar",
    "Policies",
    "Reviews"
  ]

  useEffect(()=>{
    console.log(Constants.MOBILE_WIDTH)
    setIsMobile(width<=Constants.MOBILE_WIDTH)
  },[width]);

  useEffect(() => {
    setButtonStyle(width < 500 ? selectorSingleColumnButtonStyle : selectorButtonStyle);
  }, [width]);

  useEffect(() => {
    let pHash = hash(provider);
    if(providerHash!==null && providerHash !== pHash) {
      if (!addNew) {
        clearTimeout(saveTimeoutHandle);
        setIsSaved(false);
        setSaveError(false);
        setSaveTimeoutHandle(setTimeout(
            () => {
              saveProvider();
            }, 5000));
      }
    }
    if(provider!=null) setProviderHash(pHash);
  }, [provider]);

  const sanitizeProviderForSave = (providerToSave) => {
    if (!providerToSave?.servicesProvided) {
      return providerToSave;
    }

    const validServices = providerToSave.servicesProvided.filter(service =>
        service?.category?.trim() && service?.name?.trim()
    );

    if (validServices.length === providerToSave.servicesProvided.length) {
      return providerToSave;
    }

    return {
      ...providerToSave,
      servicesProvided: validServices
    };
  }

  const saveProvider = () => {
    setNumberOfUpdates(numberOfUpdates + 1);
    const providerToSave = sanitizeProviderForSave(provider);
    if (providerToSave !== provider) {
      setProvider(providerToSave);
    }
    setSaving(true);
    ProvidersService.updateProvider(providerToSave)
      .then(response => {
        if (response && response.status >= 200 && response.status < 300) {
          setIsSaved(true);
          setSaveError(false);
        } else {
          setSaveError(true);
        }
        setSaving(false);
      })
      .catch(error => {
        console.error('Save failed:', error);
        setSaveError(true);
        setSaving(false);
      });
  }

  const refreshProvider = () => {
    // Refresh from the canonical record, not from /me/mine, so we get exactly
    // the currently-selected business even when the user owns several.
    if (!selectedProviderId) return Promise.resolve(null);
    return ProvidersService.getSingleProvider(selectedProviderId).then((response) =>
      setProvider(response),
    );
  };

  const removeService = async (index) => {
    const updated = {
      ...provider,
      servicesProvided: provider.servicesProvided.filter((_, i) => i !== index)
    };
    setProvider(updated);
    setSaving(true);
    const response = await ProvidersService.updateProvider(updated);
    if (response && response.status >= 200 && response.status < 300) {
      setIsSaved(true);
      setSaveError(false);
    } else {
      setSaveError(true);
    }
    setSaving(false);
    if (updated.servicesProvided.length === 0) {
      setSelectedServiceIndex(undefined);
      setSelectedTab("General");
      updateTabInUrl("General", searchParams, setSearchParams);
    } else {
      const nextIndex = Math.max(0, index - 1);
      setSelectedServiceIndex(nextIndex);
      setSelectedIndex(updated.servicesProvided[nextIndex].name);
    }
  };

  const handleSaveButtonClick = () => {
    clearTimeout(saveTimeoutHandle);
    saveProvider();
  }

  useEffect(() => {
    console.log(numberOfUpdates);
  }, [numberOfUpdates]);

  useEffect(() => {
    // Re-seed local state whenever the selected business changes (gate, header
    // switch, etc.) or after the photo-album modal closes (which can mutate
    // remote provider state).
    if (!selectedProviderId) {
      setProvider(null);
      return;
    }
    ProvidersService.getSingleProvider(selectedProviderId)
      .then((response) => setProvider(response))
      .catch((e) => console.error("Failed to load selected provider", e));
  }, [subscriberId, showModal, selectedProviderId]);

  useEffect(() => {
    // Load category configs on mount
    MasterDataService.getCategoryConfigs()
      .then((configs) => {
        if (configs && configs.length > 0) {
          setCategoryConfigs(configs);
        }
      })
      .catch((e) => console.error("Failed to load category configs", e));
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    ProvidersService.updateProvider(sanitizeProviderForSave(provider)).then(r => console.log(r));
  }

  const handleChange = (event) => {
    setProvider(provider => ({ ...provider, [event.target.name]: event.target.value }));
  }

  const handleChangeServiceData = (e, updatedService) => {
    e.preventDefault();
    const updatedServices = services.map((service) => {
      if (service.id === updatedService.id) {
        return updatedService;
      }
      return service;
    });
    setServices(updatedServices);
  };

  const handleDrawerToggle = () => {
    if (isMobile)
      setMobileOpen(!mobileOpen);
  };

  const getServiceCategoryConfig = (serviceCategory) => {
    // Match service category against config keys (case-insensitive)
    if (!serviceCategory || !categoryConfigs || categoryConfigs.length === 0) {
      return null;
    }

    // Find matching config: first try exact key match, then displayName match (case-insensitive)
    return categoryConfigs.find(
      (c) => c && (
        c.key === serviceCategory ||
        c.key.toLowerCase() === serviceCategory.toLowerCase() ||
        c.displayName.toLowerCase() === serviceCategory.toLowerCase()
      )
    );
  };


  const handleClickSubMenu = () => {
    setOpenSubMenu(!openSubMenu);
  };

  const newService = () => {
    return ({
      category: "",
      name: "",
      offeredLengths: [
        {
          length: 30,
          price: 59.99,
        }]
      ,
      description: ""
    })
  }

  const removePendingNewService = (providerToUpdate) => {
    if (!addNew || !providerToUpdate?.servicesProvided?.length) {
      return providerToUpdate;
    }

    return {
      ...providerToUpdate,
      servicesProvided: providerToUpdate.servicesProvided.slice(0, providerToUpdate.servicesProvided.length - 1)
    };
  }

  const handleAddNewService = () => {
    // Guard against missing provider or servicesProvided
    if (!provider || !provider.servicesProvided) {
      return;
    }

    clearTimeout(saveTimeoutHandle);
    let updatedProvider = { ...provider };
    if (addNew) {
      updatedProvider.servicesProvided = updatedProvider.servicesProvided.slice(0, updatedProvider.servicesProvided.length - 1);
    }
    setAddNew(true)
    setSelectedTab("Services")
    updateTabInUrl("Services", searchParams, setSearchParams)
    updatedProvider.servicesProvided.push(newService());
    setProvider(updatedProvider);
    setSelectedServiceIndex(updatedProvider.servicesProvided.length - 1);
  }

  const drawer = (
    <Box>
      <Helmet>
        <title>PairCare - Provider Profile</title>
      </Helmet>
      <Toolbar />
      <Divider />
      <List>
        {listOfTabs.map(text => (
          text === "Services" ?
            <Box key={text}>
              <ListItemButton onClick={handleClickSubMenu}>
                <ListItemIcon>
                  <ManageAccountsIcon />
                </ListItemIcon>

                <ListItemText primary={text} />
                {openSubMenu ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>

              <Collapse in={openSubMenu} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {provider?.servicesProvided?.map((service, index) => {
                    const config = getServiceCategoryConfig(service.category);
                    // API returns icon:"" for every category, so fall back to the default icon keyed by category (same as home.jsx buildCategoryButtons). Also works when no config matched.
                    const rawKey = (config && config.key) || service.category || "";
                    const iconName = (config && config.icon && config.icon.trim())
                      || DEFAULT_CATEGORY_ICON_BY_KEY[rawKey.toLowerCase()]
                      || "";
                    const LegacyIcon = iconName && LEGACY_CATEGORY_ICONS[iconName];
                    const MuiIcon = (!LegacyIcon && iconName) ? MUI_CATEGORY_ICONS[iconName] : null;
                    return (
                    <ListItemButton
                      sx={{ pl: 5 }}
                      key={service.name}
                      selected={selectedIndex === service.name}
                      onClick={(event) => {
                        if (addNew) {
                          let updatedProvider = { ...provider };
                          updatedProvider.servicesProvided = updatedProvider.servicesProvided.slice(0, updatedProvider.servicesProvided.length - 1);
                          setProvider(updatedProvider);
                        }
                        setAddNew(false)
                        setSelectedTab(text)
                        updateTabInUrl(text, searchParams, setSearchParams)
                        setSelectedIndex(service.name)
                        setSelectedServiceIndex(index)
                        handleChangeServiceData(event, service)
                        handleDrawerToggle()
                      }}
                    >
                      <ListItemIcon>
                        {LegacyIcon ? <LegacyIcon width={24} height={24} fill="gray" stroke="gray" /> : MuiIcon ? <MuiIcon /> : <RoomServiceIcon />}
                      </ListItemIcon>

                      <ListItemText primary={service.name} />
                    </ListItemButton>
                    );
                  })}

                  <ListItemButton
                    sx={{
                      pl: 5,
                      color: "primary.main",
                      "& .MuiListItemIcon-root": { color: "primary.main" },
                      "&:hover": { backgroundColor: "rgba(10, 102, 194, 0.08)" },
                    }}
                    key="Add New"
                    selected={selectedIndex === "Add New"}
                    onClick={handleAddNewService}
                  >
                    <ListItemIcon>
                      <AddIcon />
                    </ListItemIcon>

                    <ListItemText primary="Add New" primaryTypographyProps={{ fontWeight: 600 }} />
                  </ListItemButton>

                </List>
              </Collapse>
            </Box>
            :

            <ListItem key={text} disablePadding>
              <ListItemButton
                selected={selectedTab === text}
                onClick={() => {
                  if (addNew) {
                    setProvider(provider => removePendingNewService(provider));
                    setAddNew(false);
                  }
                  setSelectedTab(text)
                  updateTabInUrl(text, searchParams, setSearchParams)
                  handleDrawerToggle()
                }}
              >
                <ListItemIcon>
                  {renderTabIcon(text)}
                </ListItemIcon>

                <ListItemText primary={text} />
              </ListItemButton>
            </ListItem>
        ))}
      </List>
      <Divider />
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box className="mainDiv" >
      <Box sx={{ display: "flex" }}>
        <Box
          component="nav"
          sx={{
            width: { sm: drawerWidth },
            flexShrink: { sm: 0 }
          }}
          aria-label="mailbox folders"
        >
          {/* Temporary drawer visible when screen is smaller than or equal to 600px*/}
          <Drawer
            container={container}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}

            ModalProps={{
              keepMounted: true
            }}
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiDrawer-paper": {
                backgroundColor: "#f7f7f7",
                boxSizing: "border-box",
                width: drawerWidth,

              }
            }}
          >
            {drawer}
          </Drawer>

          {/* Regular drawer visible when screen is bigger than 600px*/}
          <Drawer
            variant="permanent"
            open
            sx={{
              display: { xs: "none", sm: "block" },
              "& .MuiDrawer-paper": {
                backgroundColor: "#f7f7f7",
                position: "absolute",
                left: "0px",/*(width<1668)?"0px":(width-1668)/2+'px'*/
                boxSizing: "border-box",
                width: drawerWidth,
                maxHeight: "calc(100dvh - 88px)",
                marginTop: "64px",
                zIndex: "auto"
              }
            }}
          >
            {drawer}
          </Drawer>
        </Box>
      </Box>

      <Container sx={{height: "fit-content", marginTop: "5px", display: "block", alignItems: "center", justifyContent: "space-between"}}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              margin: 0,
              padding: 0,
              mr: 2,
              display: { sm: "none" },
              marginLeft: "1%",
              verticalAlign: "text-bottom",
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant={"subtitle1"}>
            {provider?.name ? `Business Profile — ${provider.name}` : "Business Profile"}
          </Typography>
        </Box>
        {ownedProviders.length > 1 && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelectedProviderId(null)}
            data-testid="switch-business-btn"
          >
            Switch Business
          </Button>
        )}
      </Container>


      {//when provider does not exist show loading tab and update when the data is pulled
      }
      <Box>
        <Box className={isMobile?"tab-content-mobile":"tab-content"}>
          {(provider && selectedTab === "Dashboard") &&
            <DashboardTab provider={provider} onAddService={handleAddNewService} />
          }

          {(provider && selectedTab === "General") &&
            <GeneralTab setShowModal={setShowModal} onSubmit={handleSubmit} setProvider={setProvider} provider={provider} onChange={handleChange} />
          }

          {(provider && selectedTab === "Photos") &&
            <UploadPhotosTab providerId={provider.id} />
          }

          {(selectedTab === "Services") &&
            <ServicesProvidedTab addNew={addNew}
              provider={provider}
              setProvider={setProvider}
              refreshProvider={refreshProvider}
              buttonStyle={buttonStyle}
              selectedServiceIndex={selectedServiceIndex}
              setSelectedServiceIndex={setSelectedServiceIndex}
              setAddNew={setAddNew}
              removeService={removeService}
              clearAutoSave={() => clearTimeout(saveTimeoutHandle)}
            />
          }

          {(provider && selectedTab === "Promotions") &&
            <PromotionsTab />
          }

          {(provider && selectedTab === "Payments") &&
            <PaymentsTab provider={provider} setProvider={setProvider} />
          }

          {(provider && selectedTab === "Certifications") &&
            <CertificationsTab />
          }

          {(provider && selectedTab === "Calendar") &&
            <CalendarTab provider={provider} onProviderUpdate={setProvider} />
          }

          {(provider && selectedTab === "Policies") &&
            <PoliciesTab provider={provider} setProvider={setProvider} />
          }

          {(provider && selectedTab === "Social") &&
            <SocialTab />
          }

          {(provider && selectedTab === "Reviews") &&
            <ReviewsTab provider={provider} />
          }

          {/* <Button onClick={handleSaveButtonClick} disabled={isSaved} style={{ marginLeft: "auto", display: "flex", marginTop: "10px" }} variant={"outlined"} >Save</Button> */}
        </Box>
      </Box>

      <Snackbar
        open={true}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          pointerEvents: 'none',
          ...(isMobile && { height: '45px', left: '66px', right: '92px', top: '13px', transform: 'none' }),
        }}
      >
        <Alert
          severity={getSaveStateMessage().severity}
          sx={{
            width: '100%',
            pointerEvents: 'none',
            ...(isMobile && { height: '45px', alignItems: 'center' }),
          }}
          data-testid="save-toast"
        >
          {getSaveStateMessage().message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile;
